/**
 * Curated screen descriptions for the chatbot "this screen" help.
 * Texts live in i18n under `chatbot.screens.<id>.{title,objective,summary,tips}` (`tips` optional).
 * Route patterns use ":param" for any single segment.
 */
export interface ChatbotScreen {
  id: string;
  routes: ReadonlyArray<string>;
}

const REQUEST_QUEUE_ROUTES: ReadonlyArray<string> = [
  "alarmas/limpiar-alarmas/:id",
  "alarmas/config-horaria-maddalena/:id",
  "dispositivos/actualizar-sf-adr/:id",
  "dispositivos/actualizar-sf/:id",
  "dispositivos/limpiar-redundantes/:id",
  "dispositivos/cambiar-agrupacion/:id",
  "dispositivos/cambiar-entidad/:id",
  "dispositivos/desactivar/:id",
  "dispositivos/walk-by/:id",
  "dispositivos/desasignar-cups/:id",
  "dispositivos/activar/:id",
  "dispositivos/divorciar/:id",
  "dispositivos/eliminar/:id",
  "dispositivos/desasignar/:id",
  "gateways/asignar-mejor/:id",
  "gateways/asignar-mejor-redundante/:id",
  "gateways/reasignar/:id",
  "gateways/reasignar-manualmente/:id",
  "gateways/asignar-desasignar/:id",
  "gateways/refrescar/:id",
  "gateways/guardar-app-eui/:id",
  "gateways/configurar-canales/:id",
  "gateways/sniffer/:id",
  "gateways/detalle/relanzar-tramas/proceso/:id",
  "mantenimiento/red/optimizar/:id",
  "mantenimiento/red/asignar-mejor/:id"
];

/** Curated screen list the chatbot matches against the current route. */
export const CHATBOT_SCREENS: ReadonlyArray<ChatbotScreen> = [
  // Home
  { id: "home-agrupation", routes: ["principal-agrupacion"] },
  { id: "home-entities", routes: ["principal-entidades"] },
  { id: "home-global", routes: ["principal-global"] },

  // Device / gateway / CUPS details
  {
    id: "meter-detail",
    routes: [
      "dispositivos/detalle/contador/:id",
      "dispositivos/detalle/sensor/:id",
      "dispositivos/detalle/sensor-presion/:id",
      "dispositivos/detalle/sensor/fidegas/:id"
    ]
  },
  { id: "concentrator-detail", routes: ["dispositivos/detalle/lw-mbus/:id", "dispositivos/detalle/lw-une/:id"] },
  { id: "concentrator-commands", routes: ["dispositivos/detalle/lw-mbus/cola-comandos/:id"] },
  { id: "gateway-detail", routes: ["gateways/detalle/gateway/:id"] },
  { id: "cups-detail", routes: ["cups/detalle/:id"] },

  // Logs
  { id: "event-log", routes: ["dispositivos/detalle/log/eventos/:id", "gateways/detalle/log/eventos/:id"] },
  {
    id: "frames-log",
    routes: [
      "dispositivos/detalle/log/tramas/:id",
      "dispositivos/detalle/log/tramas-externo/:id",
      "dispositivos/detalle/log/tramas-nbiot/:id",
      "gateways/detalle/log/tramas/:id"
    ]
  },
  { id: "communications-log", routes: ["dispositivos/detalle/log/comunicaciones/:id"] },
  {
    id: "user-log",
    routes: [
      "dispositivos/detalle/log/usuarios/:id",
      "gateways/detalle/log/usuarios/:id",
      "cups/log/usuarios/:id",
      "agrupacion/log/usuarios",
      "usuarios/log/global",
      "usuarios/log/agrupacion"
    ]
  },
  { id: "gateway-datalogger", routes: ["gateways/detalle/log/datalogger/:id"] },
  { id: "gateway-resets", routes: ["gateways/detalle/log/reset/:id"] },

  // Device sub-screens
  {
    id: "pending-orders",
    routes: ["dispositivos/detalle/ordenes/:id", "gateways/detalle/ordenes/:id", "cups/detalle/ordenes/:id"]
  },
  {
    id: "device-alarms",
    routes: ["dispositivos/detalle/alarmas/listado/:id", "cups/alarmas/:id", "gateways/detalle/alarmas/:id"]
  },
  { id: "device-alarms-config", routes: ["dispositivos/detalle/alarmas/configuracion/:id", "cups/configuracion/:id"] },
  { id: "gateway-configure", routes: ["gateways/detalle/configurar/:id"] },
  { id: "gateway-firmware", routes: ["gateways/detalle/actualizar/firmware/:id"] },
  { id: "gateway-maintenance", routes: ["gateways/detalle/mantenimiento/:id"] },
  { id: "gateway-operator", routes: ["gateways/detalle/operator/:id"] },
  { id: "gateway-files", routes: ["gateways/detalle/files/:id"] },
  { id: "gateway-load", routes: ["gateways/detalle/load/:id"] },
  { id: "gateway-coverage-contrast", routes: ["gateways/detalle/contraste-cobertura/:id"] },
  { id: "gateway-relaunch-frames", routes: ["gateways/detalle/relanzar-tramas/:id"] },
  { id: "request-queue", routes: REQUEST_QUEUE_ROUTES },

  // Devices and orders
  { id: "meters-assigned", routes: ["dispositivos/listado/asignados"] },
  { id: "device-new", routes: ["dispositivos/nuevo"] },
  { id: "orders-list", routes: ["ordenes/listado"] },
  { id: "orders-routes", routes: ["ordenes/rutas"] },

  // Sensory
  { id: "sensory-leaks", routes: ["sensorica/fugas"] },
  { id: "sensory-pressure", routes: ["sensorica/presion"] },
  { id: "sensory-flow", routes: ["sensorica/caudal"] },

  // Alarms
  { id: "alarms-list", routes: ["alarmas/agrupacion/listado"] },
  { id: "alarms-global", routes: ["alarmas/global/listado"] },
  { id: "alarms-config", routes: ["alarmas/agrupacion/configuracion"] },
  {
    id: "alarms-custom",
    routes: ["alarmas/personalizadas/configuracion", "alarmas/personalizadas/configuracion/contador/:id"]
  },
  { id: "alarms-devices", routes: ["alarmas/dispositivos"] },

  // Telecontrol
  { id: "telecontrol-alerts", routes: ["telecontrol/alertas-activas"] },
  { id: "telecontrol-historic", routes: ["telecontrol/historico"] },
  { id: "supply-network", routes: ["telecontrol/esquema-red", "mantenimiento/construccion-de-red"] },

  // Coverage
  { id: "coverage-test", routes: ["cobertura/test"] },
  { id: "coverage-map", routes: ["cobertura/mapa", "cobertura/mapa-mbus"] },

  // Data analysis
  { id: "meter-graph", routes: ["analisis-datos/grafica"] },
  { id: "balance-list", routes: ["analisis-datos/balance"] },
  { id: "balance-group", routes: ["analisis-datos/grupo/:id"] },
  { id: "balance-form", routes: ["analisis-datos/nuevo", "analisis-datos/editar/:id"] },
  { id: "queries", routes: ["analisis-datos/consultas"] },
  { id: "consumption-evolution", routes: ["analisis-datos/evolucion-consumo"] },
  { id: "graph-comparison", routes: ["analisis-datos/comparativa-graficas"] },
  { id: "advanced-analytics", routes: ["analisis-datos/analitica-avanzada"] },
  { id: "models-edit", routes: ["analisis-datos/modelos-analiticos/editar"] },
  { id: "models-patterns", routes: ["analisis-datos/modelos-analiticos/patrones"] },
  { id: "models-meters", routes: ["analisis-datos/modelos-analiticos/listado"] },
  { id: "associations", routes: ["analisis-datos/asociaciones"] },
  {
    id: "association-form",
    routes: ["analisis-datos/asociaciones/nuevo", "analisis-datos/asociaciones/editar/:id"]
  },
  { id: "association-detail", routes: ["analisis-datos/asociaciones/detalle/:id"] },

  // Network state
  { id: "network-agrupation", routes: ["estado-red/agrupacion"] },
  { id: "network-entity", routes: ["estado-red/entidad"] },
  { id: "network-kpis", routes: ["estado-red/kpis"] },
  { id: "network-global", routes: ["estado-red/global"] },
  { id: "network-summary", routes: ["estado-red/resumen-global"] },

  // Maintenance
  { id: "global-search", routes: ["mantenimiento/global/busqueda-global"] },
  { id: "model-search", routes: ["mantenimiento/global/busqueda-por-modelo"] },

  // Users and structure
  { id: "users-list", routes: ["usuarios/listado"] },
  { id: "user-form", routes: ["usuarios/formulario/nuevo", "usuarios/formulario/editar/:id"] },
  { id: "users-import", routes: ["usuarios/importar"] },
  { id: "client-form", routes: ["clientes/formulario/nuevo", "clientes/formulario/editar/:id"] },
  { id: "entity-form", routes: ["entidades/formulario/nuevo", "entidades/formulario/editar/:id"] },
  { id: "agrupation-form", routes: ["agrupaciones/formulario/nuevo", "agrupaciones/formulario/editar/:id"] },

  // Data management and files
  { id: "cups-list", routes: ["gestion-datos/cups/listado"] },
  { id: "data-info", routes: ["gestion-datos/informacion"] },
  { id: "data-import", routes: ["gestion-datos/importar/:element"] },
  { id: "files-download", routes: ["ficheros/descarga"] },
  { id: "templates-list", routes: ["ficheros/plantillas/listado"] },
  { id: "template-form", routes: ["ficheros/plantillas/nuevo", "ficheros/plantillas/editar/:id"] },

  // Installer and subscriber
  { id: "installer-activation", routes: ["instalador/activacion"] },
  { id: "installer-historic", routes: ["instalador/historico"] },
  { id: "subscriber-main", routes: ["abonado/principal"] },
  { id: "subscriber-config", routes: ["abonado/configuracion"] }
];

/** Map types with dedicated interaction tips in `chatbot.screen-help.map-type.<type>`. */
export const CHATBOT_MAP_TYPES_WITH_TIPS: ReadonlySet<string> = new Set([
  "leakDetection",
  "pressureDetection",
  "flowDetection",
  "control",
  "coverageMap",
  "coverageMbusMap"
]);
